import { useState, useEffect, useCallback } from 'react';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

const VAPID_PUBLIC_KEY = 'BMRyUpy3EYK3h2ZwU-8x2Z3y3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3z3'; // placeholder, will be replaced by actual key from env if needed

interface UseWebPushReturn {
  subscribed: boolean;
  subscribe: (sessionId: string) => Promise<void>;
  unsubscribe: () => Promise<void>;
  supported: boolean;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useWebPush(): UseWebPushReturn {
  const [subscribed, setSubscribed] = useState(false);
  const [supported] = useState(() =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setSubscribed(!!sub);
      });
    });
  }, [supported]);

  const subscribe = useCallback(async (sessionId: string) => {
    if (!supported) return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // Try to fetch VAPID key from backend if endpoint exists, otherwise use placeholder
      let vapidKey = VAPID_PUBLIC_KEY;
      try {
        const res = await fetch(`${API_BASE}/push/vapid-public-key`);
        if (res.ok) {
          const data = await res.json();
          if (data.publicKey) vapidKey = data.publicKey;
        }
      } catch {
        // ignore
      }
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as BufferSource,
      });
    }
    const subJson = sub.toJSON();
    await fetch(`${API_BASE}/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        subscription: subJson,
      }),
    });
    setSubscribed(true);
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const subJson = sub.toJSON();
      await fetch(`${API_BASE}/push/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subJson.endpoint }),
      });
      await sub.unsubscribe();
    }
    setSubscribed(false);
  }, [supported]);

  return { subscribed, subscribe, unsubscribe, supported };
}
